import { Record as record, List } from 'immutable';

import config from '../../../src/config';
import trainTime from '../../../src/lib/TrainTime';
import { construct, resolve, resolveAll, fields } from '../../../src/helpers/LinkMapper';
import { getDefaults, getResolvers } from '../../../src/helpers/Records';
import BaseStore from '../../../src/stores/BaseStore';
import { ActionTypes } from '../../../src/constants/AppConstants';
import Claim from '../records/delayRepay/Claim';
import ClaimForm from '../records/autoDelayRepay/ClaimForm';
import { APIPager } from '../../../src/helpers/Pager';

const stateSchema = {
    loading: [ fields.bool, false ],
    loadingError: [ fields.bool, false ],
    submitting: [ fields.bool, false ],
    authorizing: [ fields.bool, false ],
    claims: [ resolveAll.as(Claim), new List() ],
    totalClaims: [ fields.number ],
    totalValue: [ fields.number ],
    oldestClaimDate: [ fields.datetime ],
    claimForm: [ resolve.as(ClaimForm) ]
};

class AutoDrState extends record(getDefaults(stateSchema)) {
    static fromJSON(json = {}) {
        return construct(AutoDrState, json, getResolvers(stateSchema));
    }
}

export class AutoDelayRepayStore extends BaseStore {

    getInitialState () {
        return new AutoDrState();
    }

    getPagination (limit = 10) {
        const { claims, totalClaims: total } = this.state;
        const page = Math.ceil(claims.size / limit) || 1;
        return APIPager.fromJSON({ limit, page, total });
    }

    getLastClaimDate (claims = this.state.claims) {
        const lastClaim = claims.size && claims.last();
        return lastClaim ? lastClaim.journey.departureDateTime : null;
    }

    getNextExpiryDate () {
        const lastClaimDate = this.state.oldestClaimDate || this.getLastClaimDate();
        const maxTicketDays = config.get('delayRepayMaxTicketDays');
        return lastClaimDate &&
            trainTime(lastClaimDate).add(maxTicketDays, 'days');
    }

    getActionHandlers () {
        return {
            [ActionTypes.LOGGED_OUT]: this.resetState,
            [ActionTypes.AUTO_DR_CLAIMS_LOADING]: this.handleClaimsLoading,
            [ActionTypes.AUTO_DR_CLAIMS_LOADED]: this.handleClaimsLoaded,
            [ActionTypes.AUTO_DR_CLAIMS_FAILED]: this.handleClaimsFailed,
            [ActionTypes.AUTO_DR_CLAIM_SUBMIT_STARTED]: this.handleClaimSubmit,
            [ActionTypes.AUTO_DR_CLAIM_SUBMITTED]: this.handleClaimSubmitted,
            [ActionTypes.AUTO_DR_CLAIM_SUBMIT_FAILED]: this.handleClaimSubmitCancelled,
            [ActionTypes.AUTO_DR_CLAIM_SUBMIT_CANCELLED]: this.handleClaimSubmitCancelled,
            [ActionTypes.AUTO_DR_CLAIM_DISMISSED]: this.handleClaimRemoved,
            [ActionTypes.AUTO_DR_CLAIM_SELECTED]: this.handleClaimSelected,
            [ActionTypes.AUTO_DR_CLAIM_DESELECTED]: this.handleClaimDeselected,
            [ActionTypes.AUTO_DR_CLAIM_FORM_UPDATED]: this.handleClaimFormUpdated,
            [ActionTypes.AUTO_DR_CLAIM_BACS_VALIDATED]: this.handleBacsValidated
        };
    }

    handleClaimsLoading () {
        this.setState(this.state.merge({
            loading: true,
            loadingError: false
        }));
    }

    handleClaimsLoaded ({ claims: newClaims, pagination, totalValue, oldestClaimDate }) {
        const { state } = this;
        const { claims } = state;
        const currentClaims = claims.map(c => c.claimRef).toSetSeq();
        newClaims = newClaims.filter(c => !currentClaims.includes(c.claimRef));

        this.setState(
            state.merge({
                loading: false,
                loadingError: false,
                oldestClaimDate,
                totalValue,
                totalClaims: pagination.totalCount,
                claims: claims.concat(newClaims),
            })
        );
    }

    handleClaimsFailed ({ error }) {
        this.setState(this.state.merge({
            loading: false,
            loadingError: Boolean(error)
        }));
    }

    handleClaimSubmit () {
        this.setState(this.state.set('submitting', true));
    }

    handleClaimSubmitCancelled () {
        this.setState(this.state.merge({
            submitting: false,
            authorizing: false
        }));
    }

    handleClaimSubmitted (payload) {
        this.handleClaimRemoved(payload);
    }

    handleClaimRemoved ({ claim }) {
        const claims = this.state.claims.filter(storedClaim => storedClaim !== claim);
        const totalClaims = this.state.totalClaims - 1;
        const totalValue = this.state.totalValue - claim.compensationAmount;

        let { oldestClaimDate } = this.state;
        const resetOldestClaimDate = !claims.size || claims.size === totalClaims;
        if (resetOldestClaimDate) {
            oldestClaimDate = this.getLastClaimDate(claims);
        }

        const updatedState = this.state.merge({
            claims,
            totalClaims,
            totalValue,
            oldestClaimDate,
            claimForm: null,
            submitting: false,
            authorizing: false
        });
        this.setState(updatedState);
    }

    handleClaimSelected ({ claim, user }) {
        const claimForm = ClaimForm.create(claim, user);
        this.setState(this.state.set('claimForm', claimForm));
    }

    handleClaimDeselected () {
        this.setState(this.state.set('claimForm', null));
    }

    handleClaimFormUpdated ({ field, value }) {
        let updated = this.state.claimForm;
        if (field.length === 2 && field[1] === 'paymentMethod') {
            updated = updated.setIn(['payment', 'bacsValidated'], false);
        }
        updated = updated.setIn(field, value);
        this.setState(this.state.set('claimForm', updated));
    }

    handleBacsValidated ({ bacsValidated, sortCode, accountNumber }) {
        const { claimForm } = this.state;
        const updatedPayment = claimForm.get('payment')
            .set('bacsValidated', bacsValidated)
            .set('bacsSortCode', sortCode)
            .set('bacsAccountNumber', accountNumber);
        const updated = claimForm.set('payment', updatedPayment);
        this.setState(this.state.set('claimForm', updated));
    }
}

const instance = new AutoDelayRepayStore();

export default instance;
